// Turns a base64 attachment payload (as returned by the workspace attachment
// endpoints) into a Blob and opens it in a new tab. Falls back to a download if
// the browser won't display the type inline.

function base64ToBlob(base64: string, mimeType: string): Blob {
  // Payload may be a data URL ("data:application/pdf;base64,....") or bare base64.
  const comma = base64.indexOf(',');
  const raw = base64.startsWith('data:') && comma !== -1 ? base64.slice(comma + 1) : base64;
  const bytes = atob(raw);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mimeType || 'application/octet-stream' });
}

export function openAttachment(payload: {
  fileData: string;
  fileName?: string;
  mimeType?: string;
}) {
  const blob = base64ToBlob(payload.fileData, payload.mimeType ?? '');
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener');
  if (!win) {
    // Popup blocked — force a download instead.
    const a = document.createElement('a');
    a.href = url;
    a.download = payload.fileName ?? 'attachment';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Revoke a bit later so the new tab has time to load.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Opens a new tab containing just the file, then triggers the browser's
// print dialog once it's loaded — a PDF renders in an <iframe> (the native
// PDF viewer most browsers show there has its own print control, and the
// parent window's print() call forwards to it for a same-origin blob: URL);
// an image renders directly so the browser's standard image print applies.
export function printAttachment(payload: {
  fileData: string;
  fileName?: string;
  mimeType?: string;
}) {
  const mimeType = payload.mimeType || 'application/octet-stream';
  const blob = base64ToBlob(payload.fileData, mimeType);
  const url = URL.createObjectURL(blob);
  const isImage = mimeType.startsWith('image/');
  const title = escapeHtml(payload.fileName ?? 'Print');

  const win = window.open('', '_blank', 'noopener');
  if (!win) {
    // Popup blocked — fall back to a plain open so the user can print from there.
    openAttachment(payload);
    return;
  }

  win.document.write(
    isImage
      ? `<html><head><title>${title}</title></head><body style="margin:0"><img src="${url}" style="width:100%" /></body></html>`
      : `<html><head><title>${title}</title></head><body style="margin:0"><iframe src="${url}" style="border:0;width:100%;height:100vh"></iframe></body></html>`,
  );
  win.document.close();

  const triggerPrint = () => {
    try {
      win.focus();
      win.print();
    } catch {
      // Printing is best-effort — the tab is still open for a manual print.
    }
  };
  const target = win.document.querySelector(isImage ? 'img' : 'iframe');
  if (target) target.addEventListener('load', triggerPrint);
  else setTimeout(triggerPrint, 500);

  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
